import { z } from 'zod';
// Item Type enum
export const roadmapItemTypeEnum = z.enum([
    'improvement',
    'new_feature',
    'refactor',
    'research',
    'tech_debt',
    'bug',
    'spike',
    'idea',
]);
// Item Status enum
export const roadmapItemStatusEnum = z.enum([
    'inbox',
    'triaged',
    'planned',
    'in_progress',
    'in_review',
    'done',
    'archived',
    'blocked',
]);
// Initiative Status enum
export const roadmapInitiativeStatusEnum = z.enum(['active', 'paused', 'archived']);
// Dependency Kind enum
export const roadmapDependencyKindEnum = z.enum(['blocks', 'enables', 'relates', 'duplicates']);
// Atlas Link Kind enum
export const roadmapAtlasLinkKindEnum = z.enum(['touches', 'requires', 'adds']);
// Item CREATE schema
export const roadmapItemCreateSchema = z.object({
    parentId: z.coerce.number().int().positive().optional().nullable(),
    initiativeId: z.coerce.number().int().positive().optional().nullable(),
    phaseId: z.coerce.number().int().positive().optional().nullable(),
    type: roadmapItemTypeEnum.optional().default('idea'),
    status: roadmapItemStatusEnum.optional().default('inbox'),
    title: z.string().min(1, 'Title is required').max(500),
    descriptionMd: z.string().optional(),
    acceptanceMd: z.string().optional(),
    priority: z.coerce.number().int().min(1).max(5).optional().default(3),
    impact: z.coerce.number().int().min(1).max(5).optional().default(3),
    effort: z.coerce.number().int().min(1).max(5).optional().default(3),
    aiGenerated: z.boolean().optional().default(false),
    sourceCanvasId: z.coerce.number().int().positive().optional().nullable(),
    targetQuarter: z.string().max(20).optional().nullable(),
    ownerProfileId: z.coerce.number().int().positive().optional().nullable(),
    assignedTo: z.coerce.number().int().positive().optional().nullable(),
    autodevReady: z.boolean().optional().default(false),
    position: z.object({ x: z.number(), y: z.number() }).optional().nullable(),
    color: z.string().max(20).optional().nullable(),
    metadata: z.record(z.unknown()).optional(),
});
// Item UPDATE schema
export const roadmapItemUpdateSchema = z.object({
    parentId: z.coerce.number().int().positive().optional().nullable(),
    initiativeId: z.coerce.number().int().positive().optional().nullable(),
    phaseId: z.coerce.number().int().positive().optional().nullable(),
    type: roadmapItemTypeEnum.optional(),
    status: roadmapItemStatusEnum.optional(),
    title: z.string().min(1).max(500).optional(),
    descriptionMd: z.string().optional().nullable(),
    acceptanceMd: z.string().optional().nullable(),
    priority: z.coerce.number().int().min(1).max(5).optional(),
    impact: z.coerce.number().int().min(1).max(5).optional(),
    effort: z.coerce.number().int().min(1).max(5).optional(),
    targetQuarter: z.string().max(20).optional().nullable(),
    ownerProfileId: z.coerce.number().int().positive().optional().nullable(),
    assignedTo: z.coerce.number().int().positive().optional().nullable(),
    autodevReady: z.boolean().optional(),
    position: z.object({ x: z.number(), y: z.number() }).optional().nullable(),
    color: z.string().max(20).optional().nullable(),
    metadata: z.record(z.unknown()).optional(),
});
// Item LIST QUERY schema
export const roadmapItemListQuerySchema = z.object({
    page: z.coerce.number().int().positive().optional().default(1),
    perPage: z.coerce.number().int().min(1).max(2000).optional().default(20),
    search: z.string().optional(), // Search in title/descriptionMd
    type: roadmapItemTypeEnum.optional(),
    status: roadmapItemStatusEnum.optional(),
    initiativeId: z.coerce.number().int().positive().optional(),
    phaseId: z.coerce.number().int().positive().optional(),
    ownerProfileId: z.coerce.number().int().positive().optional(),
    excludeDeleted: z.coerce.boolean().optional().default(true),
    sortBy: z.enum(['created_at', 'updated_at', 'priority', 'impact', 'effort', 'vote_score']).optional().default('updated_at'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});
// Initiative CREATE schema
export const roadmapInitiativeCreateSchema = z.object({
    slug: z.string().max(100).optional(), // Auto-generated if not provided
    title: z.string().min(1, 'Title is required').max(255),
    visionMd: z.string().optional(),
    color: z.string().max(20).optional().default('#6366f1'),
    sort: z.coerce.number().int().optional().default(0),
    ownerProfileId: z.coerce.number().int().positive().optional().nullable(),
    status: roadmapInitiativeStatusEnum.optional().default('active'),
});
// Initiative UPDATE schema
export const roadmapInitiativeUpdateSchema = z.object({
    slug: z.string().max(100).optional(),
    title: z.string().min(1).max(255).optional(),
    visionMd: z.string().optional().nullable(),
    color: z.string().max(20).optional(),
    sort: z.coerce.number().int().optional(),
    ownerProfileId: z.coerce.number().int().positive().optional().nullable(),
    status: roadmapInitiativeStatusEnum.optional(),
});
// Phase CREATE schema
export const roadmapPhaseCreateSchema = z.object({
    slug: z.string().max(100).optional(), // Auto-generated if not provided
    title: z.string().min(1, 'Title is required').max(255),
    descriptionMd: z.string().optional(),
    sort: z.coerce.number().int().optional().default(0),
    color: z.string().max(20).optional().default('#8b5cf6'),
    targetQuarter: z.string().max(20).optional().nullable(),
    rangeStart: z.string().datetime().optional().nullable(),
    rangeEnd: z.string().datetime().optional().nullable(),
});
// Phase UPDATE schema
export const roadmapPhaseUpdateSchema = z.object({
    slug: z.string().max(100).optional(),
    title: z.string().min(1).max(255).optional(),
    descriptionMd: z.string().optional().nullable(),
    sort: z.coerce.number().int().optional(),
    color: z.string().max(20).optional(),
    targetQuarter: z.string().max(20).optional().nullable(),
    rangeStart: z.string().datetime().optional().nullable(),
    rangeEnd: z.string().datetime().optional().nullable(),
});
// Dependency CREATE schema
export const roadmapDependencyCreateSchema = z.object({
    fromId: z.coerce.number().int().positive(),
    toId: z.coerce.number().int().positive(),
    kind: roadmapDependencyKindEnum.optional().default('blocks'),
    metadata: z.record(z.unknown()).optional(),
});
// Atlas Link CREATE schema
export const roadmapAtlasLinkCreateSchema = z.object({
    itemId: z.coerce.number().int().positive(),
    atlasNodeId: z.coerce.number().int().positive(),
    kind: roadmapAtlasLinkKindEnum.optional().default('touches'),
});
// Promote to Issue schema
export const roadmapPromoteSchema = z.object({
    itemId: z.coerce.number().int().positive(),
    autoStartDev: z.boolean().optional().default(false),
});
//# sourceMappingURL=roadmap.schema.js.map